/**
 * Admin-only modal that lists users this actor can impersonate, and starts
 * a session when one is picked. D-074 (2026-05-22)
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/session";
import {
  setImpersonationTarget,
  clearScopedSession,
  type ImpersonationTarget,
} from "@/lib/impersonation";
import { queryClient } from "@/App";

interface AvailableTarget {
  id: string;
  email: string;
  name: string;
  role: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const ROLE_LABEL: Record<string, string> = {
  manager: "Manager",
  bdr: "BDR",
  ae: "AE",
  executive: "Executive",
  marketing: "Marketing",
  se: "Sales Engineer",
};

export function ImpersonationPicker({ open, onClose }: Props) {
  const [targets, setTargets] = useState<AvailableTarget[]>([]);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    api<{ targets: AvailableTarget[] }>("/api/admin/impersonate/available-targets")
      .then((r) => setTargets(r.targets || []))
      .catch((e) => setError(e?.message || "Could not load users."))
      .finally(() => setLoading(false));
  }, [open]);

  async function handleStart(t: AvailableTarget) {
    setStarting(t.id);
    setError(null);
    try {
      const resp = await api<{
        session_id: string;
        expires_at: string;
        target: { id: string; email: string; name: string; role: string; tenantId: number };
      }>("/api/admin/impersonate/start", {
        method: "POST",
        body: JSON.stringify({ target_user_id: t.id }),
      });
      const next: ImpersonationTarget = {
        id: resp.target.id,
        email: resp.target.email,
        name: resp.target.name,
        role: resp.target.role as ImpersonationTarget["role"],
        tenantId: resp.target.tenantId,
        expiresAt: resp.expires_at,
        sessionId: resp.session_id,
      };
      setImpersonationTarget(next);
      // Wave I (B-BDR6): wipe React Query cache and role-scoped localStorage
      // before reloading so no stale prior-identity data surfaces under the
      // new effective user.
      queryClient.clear();
      clearScopedSession();
      onClose();
      // Hard reload so every page reads fresh data as the new effective user.
      window.location.reload();
    } catch (e: any) {
      setError(e?.message || "Could not start impersonation.");
      setStarting(null);
    }
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Impersonate user"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9998,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#FFFFFF",
          color: "#0B0B0F",
          borderRadius: 8,
          width: "100%",
          maxWidth: 560,
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
        }}
      >
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #E5E7EB",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Impersonate a user</div>
            <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>
              You will see the platform exactly as they see it. Every action is audit-logged. Session ends after 60 minutes.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent",
              border: "none",
              fontSize: 20,
              cursor: "pointer",
              color: "#6B7280",
              padding: 4,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ overflow: "auto", padding: 8 }}>
          {loading && (
            <div style={{ padding: 24, textAlign: "center", color: "#6B7280", fontSize: 13 }}>
              Loading users...
            </div>
          )}
          {error && (
            <div
              style={{
                margin: 12,
                padding: 12,
                background: "#FEF2F2",
                color: "#7F1D1D",
                border: "1px solid #FECACA",
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}
          {!loading && targets.length === 0 && !error && (
            <div style={{ padding: 24, textAlign: "center", color: "#6B7280", fontSize: 13 }}>
              No users are available to impersonate.
            </div>
          )}
          {!loading &&
            targets.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => handleStart(t)}
                disabled={starting !== null}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 14px",
                  background: starting === t.id ? "#F3F4F6" : "transparent",
                  border: "none",
                  borderBottom: "1px solid #F3F4F6",
                  textAlign: "left",
                  cursor: starting ? "wait" : "pointer",
                  fontSize: 13,
                  color: "#0B0B0F",
                  opacity: starting && starting !== t.id ? 0.5 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!starting) (e.currentTarget as HTMLButtonElement).style.background = "#F9FAFB";
                }}
                onMouseLeave={(e) => {
                  if (starting !== t.id) (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{t.name}</div>
                  <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>{t.email}</div>
                </div>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    padding: "3px 8px",
                    borderRadius: 4,
                    background: "#F3F4F6",
                    color: "#374151",
                    whiteSpace: "nowrap",
                    marginLeft: 12,
                  }}
                >
                  {ROLE_LABEL[t.role] || t.role}
                </span>
              </button>
            ))}
        </div>
      </div>
    </div>
  );
}
